import { parseISO } from "date-fns";
import { useFormik } from "formik";
import React, { Fragment, useCallback, useState } from "react";
import { Helmet } from "react-helmet";

import {
  Column,
  Container,
  Icon,
  Pressable,
  Row,
  useToast,
} from "../components";
import { Location, OrderStatus, Space } from "../data";
import { PartnerOffering, TemplateName } from "../data/partner_offering";
import { useMediaQuery } from "../hooks";
import { FBT, useI18n } from "../lib/i18n/i18n_provider";
import { range } from "../lib/js_utils/array_utils";
import { isEmail } from "../lib/validation/string";
import {
  BookingType,
  invalidateOrderCache,
  invalidateOrderLineItemCache,
  InviteAttendeeInput,
  Order,
  OrderableType,
  OrderInvites,
  OrderItem,
  OrderItemDayPass,
  OrderItemHourlyPass,
  OrderItemMembership,
  OrderItemMultiPass,
  PaymentMethod,
  SendOrderInvitesInput,
  useCancelOrder,
  useOrderInviteQuery,
  useSendOrderInvites,
} from "./api";
import { Button } from "./button";
import { Dialog } from "./dialog";
import { Divider } from "./divider";
import { Footer } from "./footer";
import { Grid } from "./grid";
import { Heading } from "./heading";
import { Hidden } from "./hidden";
import { OfferingFooterMobile } from "./offering_footer_mobile";
import { OfferingHeader } from "./offering_header";
import { getMainOrderItem } from "./order";
import { OrderParams } from "./order_params";
import { PageLayout } from "./page_layout";
import { PartnerConfig } from "./partner_config";
import { QuoteView } from "./quote_view";
import { Spacer } from "./spacer";
import { Text } from "./text";
import { TextArea } from "./text_area";
import { TextInput } from "./text_input";
import { TextLink } from "./text_link";
import { offeringConfig } from "./offering_config";
import { PostPurchasePromoBox } from "./wework/post-purchase/post_purchase_promo_box";
import { getFieldFromObject } from "../offering_v2/utils/get_field_from_object";
import styles from "./template_offering_confirm_order_base.module.css";

interface TemplateOfferingConfirmOrderBaseProps {
  partnerConfig: PartnerConfig;
  confirmationMessage?: (
    order: Order,
    orderItem: OrderItem | undefined
  ) => React.ReactNode;
  space: Space;
  location: Location;
  offeringName: string;
  partnerOfferingID: string;
  partnerOffering: PartnerOffering;
  order: Order;
  showTalkWithUsPrompt?: boolean;
  includeQuantity?: boolean;
  numberOfInvites?: number;
  mobileCheckoutBox: React.ReactNode;
  desktopCheckoutBox: React.ReactNode;
}

const CANCEL_REASON =
  "Cancelled by the customer from the order confirmation screen.";

export function TemplateOfferingConfirmOrderBase(
  props: TemplateOfferingConfirmOrderBaseProps
) {
  const {
    order,
    space,
    location,
    offeringName,
    partnerOffering,
    partnerOfferingID,
    partnerConfig,
    mobileCheckoutBox,
    desktopCheckoutBox,
    numberOfInvites,
    showTalkWithUsPrompt,
    includeQuantity,
    confirmationMessage,
  } = props;
  const { hubspotForm, favicon, showLanguageSelector = false } = partnerConfig;
  const { fbt } = useI18n();
  const [viewCancelOrderConfirmation, setViewCancelOrderConfirmation] =
    useState<boolean>(false);
  const [
    viewCancelOrderMobileConfirmation,
    setViewCancelOrderMobileConfirmation,
  ] = useState<boolean>(false);
  const [disableConfirmationButton, setDisableConfirmationButton] =
    useState<boolean>(false);

  const cancelOrder = useCancelOrder();
  const toast = useToast();
  const { data: invites, refetch: refetchInvites } = useOrderInviteQuery(
    order.id
  );
  const mainOrderItem = getMainOrderItem(order);
  const orderParams = orderItemToOrderParams(mainOrderItem);

  const handleOpenDialog = useCallback(() => {
    setViewCancelOrderConfirmation(true);
  }, []);

  const handleOpenDialogCancelMobile = useCallback(() => {
    setViewCancelOrderMobileConfirmation(true);
  }, []);

  const orderItemBooking = order.orderItems.find(
    (orderItem) => orderItem.type === OrderableType.Booking
  );

  const orderItemAddon = order.orderItems.find(
    (orderItem) => orderItem.type === OrderableType.AddOn
  ) as OrderItemMultiPass;

  const orderItemID = orderItemBooking?.orderItemID
    ? orderItemBooking?.orderItemID
    : orderItemAddon?.orderItemID;

  const cancelledAt = order.cancelledAt
    ? fbt.formatDateByPattern(parseISO(order.cancelledAt), " MMM do, yyyy")
    : "";

  const handleCancelOrder = useCallback(
    async (orderID: string) => {
      try {
        setDisableConfirmationButton(true);
        const response = await cancelOrder(orderID, CANCEL_REASON);
        if (response.status === "failed") {
          toast.error({ message: response.message });
        }
      } catch (error) {
        setDisableConfirmationButton(false);
        console.error(error);
        toast.error({ message: "Could not cancel booking " });
      } finally {
        await invalidateOrderCache(orderID);
        await invalidateOrderLineItemCache(orderItemID);
        setTimeout(() => {
          setViewCancelOrderConfirmation(false);
        }, 300);
      }
    },
    [cancelOrder, toast, orderItemID]
  );

  const isWWMeetingAndClassRoom =
    partnerOffering.slug === offeringConfig.WeWork.slugConfig.meetingRoom ||
    partnerOffering.slug === offeringConfig.WeWork.slugConfig.classroom ||
    partnerOffering.slug === offeringConfig.WeWork.slugConfig.dayOffice;

  return (
    <Container>
      <Helmet>
        <title>
          {offeringName} - {partnerOffering.partner.name}
        </title>
        <link href={favicon} rel="shortcut icon" type="image/x-icon" />
      </Helmet>
      <OfferingHeader
        partnerID={partnerOffering.partner.id}
        offeringID={partnerOfferingID}
        homepageURL={partnerOffering.partner.homepageUrl}
        logo={partnerOffering.partner.logoUrl}
        hubspotForm={hubspotForm}
        showLanguageSelector={showLanguageSelector}
        localeOptions={partnerConfig.localeOptions}
        showTalkWithUsPrompt={showTalkWithUsPrompt}
      />
      <Spacer size={24} />
      <PageLayout
        content={
          <Container paddingHorizontal={16}>
            {order.status === OrderStatus.Canceled ? (
              <Fragment>
                <Heading>{fbt("Order cancelled")}</Heading>
                <Spacer size={8} />
                <Text>
                  <Text weight="bold">{fbt("Cancelled and refunded ")}</Text>
                  {fbt("on " + fbt.param("cancelledAt", cancelledAt) + ".")}
                </Text>
              </Fragment>
            ) : (
              <Fragment>
                <Heading>{fbt("Confirmed!")}</Heading>
                <Spacer size={8} />
                {confirmationMessage ? (
                  confirmationMessage(order, mainOrderItem)
                ) : (
                  <Text>
                    {fbt(
                      "Thank you for booking with us. A confirmation email has been sent to " +
                        fbt.param("email", order.email) +
                        ". We are looking forward to seeing you soon."
                    )}
                  </Text>
                )}
              </Fragment>
            )}
            {space.productType?.invites && numberOfInvites && (
              <Fragment>
                <Spacer size={16} />
                <SendInvites
                  numberOfInvites={numberOfInvites}
                  existingInvites={invites}
                  refetchInvites={refetchInvites}
                  order={order}
                  offeringName={location.name}
                />
                <Spacer size={16} />
              </Fragment>
            )}
            <Spacer size={24} />
            <Hidden md>
              <Text weight="bold">{offeringName}</Text>
              <Spacer size={16} />
              {mobileCheckoutBox}
              <QuoteView
                orderParams={orderParams}
                locationTimezone={location.timezone}
                currency={order.currencyCode}
                pricingInclusion={location.pricingInclusion}
                // todo this need to be fixed
                quote={order as any}
                includeQuantity={includeQuantity}
                orderStatus={order.status}
              />

              {order.cancellable && (
                <Container>
                  <Column alignItems="flex-start">
                    <Pressable
                      onPress={() => {
                        handleOpenDialogCancelMobile();
                      }}
                    >
                      <Text decoration="underline" color="link">
                        {fbt("Cancel Order")}
                      </Text>
                    </Pressable>
                    <Dialog
                      isOpen={viewCancelOrderMobileConfirmation}
                      onRequestClose={() =>
                        setViewCancelOrderMobileConfirmation(false)
                      }
                    >
                      <Container padding={24}>
                        <Pressable
                          onPress={() =>
                            setViewCancelOrderMobileConfirmation(false)
                          }
                        >
                          <Icon name="x" />
                        </Pressable>
                        <Spacer size={20} />
                        <Container>
                          <Heading>{fbt("Cancellation")}</Heading>
                          <Spacer size={10} />
                          <Text>
                            {fbt(
                              "Are you sure you want to cancel and get a refund? This cannot be un-done."
                            )}
                          </Text>
                        </Container>
                        <Spacer size={40} />
                        <Container>
                          <Row justifyContent="flex-end" alignItems="flex-end">
                            {disableConfirmationButton ? (
                              <Container padding={8}>
                                <Text color="muted">
                                  {fbt("No, do not cancel")}
                                </Text>
                              </Container>
                            ) : (
                              <Container padding={8}>
                                <Text
                                  color="link"
                                  decoration="underline"
                                  onPress={() =>
                                    setViewCancelOrderMobileConfirmation(false)
                                  }
                                >
                                  {fbt("No, do not cancel")}
                                </Text>
                              </Container>
                            )}
                            <Spacer direction="horizontal" size={20} />
                            <Button
                              testID="cancel-order"
                              title={fbt("Yes, Cancel & Refund")}
                              disabled={disableConfirmationButton}
                              onPress={() => handleCancelOrder(order.id)}
                            />
                          </Row>
                        </Container>
                      </Container>
                    </Dialog>
                  </Column>
                </Container>
              )}
              <Spacer size={24} />
            </Hidden>
            <Grid itemsPerRow={{ default: 1, md: 2 }}>
              <Container flex={1}>
                <OrderPaymentMethod order={order} />
                <Spacer size={24} />
                <OrderGuestDetails order={order} />
                <Spacer size={24} />
              </Container>
              <Container flex={1}>
                {order.bookingReason && (
                  <Fragment>
                    <Text color="muted" size="sm">
                      {fbt("bookingReason")}
                    </Text>
                    <Spacer size={8} />
                    <Text>{order.bookingReason}</Text>
                    <Spacer size={24} />
                  </Fragment>
                )}
                {space.bookingPolicy.askForTax && (
                  <Fragment>
                    <OrderTax order={order} />
                    <Spacer size={24} />
                  </Fragment>
                )}
                <OrderBillingDetails order={order} />
                <Spacer size={24} />
              </Container>
            </Grid>

            {isWWMeetingAndClassRoom && <PostPurchasePromoBox />}
          </Container>
        }
        rightSidebar={
          <Container>
            <Text weight="bold">{offeringName}</Text>
            <Spacer size={8} />
            <Container borderRadius={8} borderWidth={1} borderColor="#d7d5dd">
              <Container>{desktopCheckoutBox}</Container>
              <Divider />
              <Container paddingHorizontal={16}>
                <QuoteView
                  orderParams={orderParams}
                  locationTimezone={location.timezone}
                  currency={order.currencyCode}
                  pricingInclusion={location.pricingInclusion}
                  quote={order as any}
                  includeQuantity={includeQuantity}
                  orderStatus={order.status}
                />
              </Container>
              <Spacer size={16} />
            </Container>
            {order.cancellable && (
              <Container>
                <Column alignItems="flex-end">
                  <Spacer size={16} />
                  <Pressable
                    onPress={() => {
                      handleOpenDialog();
                    }}
                  >
                    <Text decoration="underline" color="link">
                      {fbt("Cancel Order")}
                    </Text>
                  </Pressable>
                  <Dialog
                    isOpen={viewCancelOrderConfirmation}
                    onRequestClose={() => setViewCancelOrderConfirmation(false)}
                  >
                    <Container minWidth={573} padding={24}>
                      <Pressable
                        onPress={() => setViewCancelOrderConfirmation(false)}
                      >
                        <Icon name="x" />
                      </Pressable>
                      <Spacer size={20} />
                      <Container>
                        <Heading>{fbt("Cancellation")}</Heading>
                        <Spacer size={10} />
                        <Text>
                          {fbt(
                            "Are you sure you want to cancel and get a refund? This cannot be un-done."
                          )}
                        </Text>
                      </Container>
                      <Spacer size={40} />
                      <Container>
                        <Row justifyContent="flex-end" alignItems="flex-end">
                          {disableConfirmationButton ? (
                            <Container padding={8}>
                              <Text color="muted">
                                {fbt("No, do not cancel")}
                              </Text>
                            </Container>
                          ) : (
                            <Container padding={8}>
                              <Text
                                color="link"
                                decoration="underline"
                                onPress={() =>
                                  setViewCancelOrderConfirmation(false)
                                }
                              >
                                {fbt("No, do not cancel")}
                              </Text>
                            </Container>
                          )}
                          <Spacer direction="horizontal" size={20} />
                          <Button
                            testID="cancel-order"
                            title={fbt("Yes, Cancel & Refund")}
                            disabled={disableConfirmationButton}
                            onPress={() => handleCancelOrder(order.id)}
                          />
                        </Row>
                      </Container>
                    </Container>
                  </Dialog>
                </Column>
              </Container>
            )}
          </Container>
        }
      />
      <Spacer size={24} />
      <OfferingFooterMobile
        showLanguageSelector={showLanguageSelector}
        hubspotForm={hubspotForm}
        partnerID={partnerOffering.partner.id}
        offeringID={partnerOfferingID}
        showTalkWithUsPrompt={showTalkWithUsPrompt}
        localeOptions={partnerConfig.localeOptions}
      />
      <Spacer size={20} />
      <Footer displayAccessibility={partnerConfig.displayAccessibility} />
    </Container>
  );
}

export function orderItemToOrderParams(
  orderItem:
    | OrderItemMultiPass
    | OrderItemDayPass
    | OrderItemHourlyPass
    | OrderItemMembership
    | undefined
) {
  let orderParams: OrderParams | undefined = undefined;

  if (orderItem?.type === OrderableType.AddOn) {
    orderParams = {
      type: TemplateName.MultiPass,
      quantity: orderItem.quantity,
      spaceID: orderItem.details.spaceID,
      addOnID: orderItem.details.addOnID,
    };
  } else {
    const details = getFieldFromObject("details", orderItem);
    const type = getFieldFromObject("type", details);
    if (type === BookingType.Daily) {
      orderParams = {
        type: TemplateName.DayPass,
        spaceID: getFieldFromObject("spaceID", details),
        quantity: orderItem?.quantity,
        dateRange: {
          startDate: getFieldFromObject("startDate", details),
          endDate: getFieldFromObject("endDate", details),
        },
      };
    } else if (type === BookingType.Hourly) {
      orderParams = {
        type: "Hourly",
        spaceID: getFieldFromObject("spaceID", details),
        dateRange: {
          date: getFieldFromObject("date", details),
          startTime: getFieldFromObject("startTime", details),
          endTime: getFieldFromObject("endTime", details),
        },
      };
    }
  }

  return orderParams;
}
interface SendInvitesProps {
  existingInvites: OrderInvites[];
  offeringName: string;
  order: Order;
  numberOfInvites: number;
  refetchInvites: () => Promise<void>;
}

export function SendInvites(props: SendInvitesProps) {
  const {
    offeringName,
    order,
    existingInvites,
    numberOfInvites,
    refetchInvites,
  } = props;
  const [open, setOpen] = useState<"view" | "invite" | "none">("none");

  const { fbt } = useI18n();
  const attendees = getOrderInvitesAttendees(existingInvites);
  const numberOfInvitesSent = attendees.length;
  const full = numberOfInvites === numberOfInvitesSent;

  return (
    <div className="confirm-order-send-invites">
      {numberOfInvitesSent > 0 ? (
        <Text>
          {fbt(
            "Invitation sent to " +
              fbt.plural("attendee", numberOfInvitesSent, {
                name: "number of attendees",
                showCount: "yes",
                many: "attendees",
              })
          )}{" "}
          (<TextLink onPress={() => setOpen("view")}>{fbt("view")}</TextLink>).
        </Text>
      ) : (
        <Text>{fbt("Will there be others joining you?")}</Text>
      )}
      {!full && (
        <Fragment>
          <Spacer size={16} direction="horizontal" />
          <Spacer size={16} />
          <Button
            override={{
              button: { style: { paddingLeft: 16, paddingRight: 16 } },
            }}
            title={fbt("Invite Attendees")}
            onPress={() => setOpen("invite")}
          />
        </Fragment>
      )}
      <Dialog isOpen={open === "invite"} onRequestClose={() => setOpen("none")}>
        <div className="confirm-order-dialog-wrapper">
          <InviteAttendeesDialogContent
            existingInvites={existingInvites}
            numberOfInvites={numberOfInvites}
            order={order}
            onComplete={async (response) => {
              if (response) {
                await refetchInvites();
                setOpen("view");
              } else {
                setOpen("none");
              }
            }}
            onClose={() => setOpen("none")}
            offeringName={offeringName}
          />
        </div>
      </Dialog>
      <Dialog isOpen={open === "view"} onRequestClose={() => setOpen("none")}>
        <div className="confirm-order-dialog-wrapper">
          <InviteAttendeesCompleteDialogContent
            existingInvites={existingInvites}
            onClose={() => setOpen("none")}
          />
        </div>
      </Dialog>
    </div>
  );
}

interface InviteAttendeesCompleteDialogContentProps {
  existingInvites: OrderInvites[];
  onClose: () => void;
}

function InviteAttendeesCompleteDialogContent(
  props: InviteAttendeesCompleteDialogContentProps
) {
  const { onClose, existingInvites } = props;
  const mq = useMediaQuery();
  const { fbt } = useI18n();
  return (
    <Fragment>
      <Pressable onPress={onClose}>
        <Icon name="x" />
      </Pressable>
      <Spacer size={24} />
      <Heading>{fbt("Invitation sent!")}</Heading>
      <Spacer size={8} />
      <Text>
        {fbt(
          "An email invitation have been sent to your attendees with details and directions."
        )}
      </Text>
      <Spacer size={24} />
      {existingInvites.map((invite) => (
        <Container>
          {invite.message && (
            <Fragment>
              <Text color="muted">{fbt("Message")}</Text>
              <Spacer size={8} />
              <Text>{invite.message}</Text>
            </Fragment>
          )}

          <Spacer size={16} />
          {invite.attendees.length > 0 && (
            <Text color="muted">{fbt("Attendees")}</Text>
          )}
          <Spacer size={8} />
          <Container borderTopWidth={1} borderColor="#D7D5DD">
            {invite.attendees.map((a, i) =>
              mq.device === "desktop" ? (
                <Container
                  key={`${a.email}${i}`}
                  height={32}
                  borderBottomWidth={1}
                  borderColor="#D7D5DD"
                >
                  <Column flex={1} justifyContent="center">
                    <Row key={a.email}>
                      <Container minWidth={200}>
                        <Text>{a.name}</Text>
                      </Container>
                      <Text>{a.email}</Text>{" "}
                      {a.phone && (
                        <Fragment>
                          <Spacer size={4} direction="horizontal" />
                          <Text> / {a.phone}</Text>
                        </Fragment>
                      )}
                    </Row>
                  </Column>
                </Container>
              ) : (
                <Container
                  key={`${a.email}${i}`}
                  paddingTop={8}
                  borderBottomWidth={1}
                  borderColor="#D7D5DD"
                >
                  <Text>{a.name}</Text>
                  <Row>
                    <Text>{a.email}</Text>
                    {a.phone && (
                      <Fragment>
                        <Spacer size={4} direction="horizontal" />
                        <Text> / {a.phone}</Text>
                      </Fragment>
                    )}
                  </Row>
                  <Spacer size={8} />
                </Container>
              )
            )}
          </Container>
          <Spacer size={24} />
        </Container>
      ))}
      <Spacer />
      <Button onPress={onClose} title={fbt("Done")} />
    </Fragment>
  );
}

interface InviteAttendeesDialogContentProps {
  order: Order;
  existingInvites: OrderInvites[];
  onComplete: (sent: boolean) => void;
  onClose: () => void;
  numberOfInvites: number;
  offeringName: string;
}

function InviteAttendeesDialogContent(
  props: InviteAttendeesDialogContentProps
) {
  const {
    onClose,
    offeringName,
    order,
    numberOfInvites,
    existingInvites,
    onComplete,
  } = props;
  const { fbt } = useI18n();
  const existingAttendees = getOrderInvitesAttendees(existingInvites);
  return (
    <Fragment>
      <Pressable onPress={onClose}>
        <Icon name="x" />
      </Pressable>
      <Spacer size={24} />
      <Heading>{fbt("Invite Attendees")}</Heading>
      <Spacer size={4} />
      {existingAttendees.length > 0 && (
        <Fragment>
          <Text color="muted">
            {fbt("Invitations already sent to:")}{" "}
            {getInvitationAttendeeListLabel(existingInvites, fbt)}
          </Text>
          <Spacer size={24} />
        </Fragment>
      )}
      <Text>
        {fbt(
          "Fill in the information below to send an email invitation to your attendees with details and directions."
        )}
      </Text>
      <Spacer size={32} />
      <InviteAttendees
        existingInvites={existingInvites}
        numberOfInvites={numberOfInvites}
        order={order}
        onComplete={onComplete}
        offeringName={offeringName}
      />
    </Fragment>
  );
}

interface InviteAttendeesProps {
  order: Order;
  offeringName: string;
  numberOfInvites: number;
  existingInvites: OrderInvites[];
  onComplete: (sent: boolean) => void;
}

function InviteAttendees(props: InviteAttendeesProps) {
  const { offeringName, numberOfInvites, existingInvites, order, onComplete } =
    props;
  const mq = useMediaQuery();
  const sendOrderInvites = useSendOrderInvites();
  const { fbt } = useI18n();
  const existingAttendees = getOrderInvitesAttendees(existingInvites);
  const inviteRange = existingInvites
    ? range(0, numberOfInvites - existingAttendees.length - 1)
    : range(0, numberOfInvites - 1);
  const initialAttendees = inviteRange.map(() => ({
    name: "",
    email: "",
    phone: "",
  }));

  const { values, setFieldValue, errors, submitForm, isSubmitting } =
    useFormik<SendOrderInvitesInput>({
      initialValues: {
        message: "",
        attendees: initialAttendees,
      },
      initialErrors: {
        message: "",
        attendees: initialAttendees,
      },
      validate: (v) => {
        const errors: SendOrderInvitesInput = {
          message: "",
          attendees: initialAttendees,
        };

        let hasError = false;

        if (v.message) {
          const filledNameOrEmail = v.attendees.some((a) => a.name || a.email);

          if (!filledNameOrEmail) {
            errors.message = fbt("Please enter at least one attendee.");
            return errors;
          }
        }

        v.attendees.forEach((a, i) => {
          if (a.name) {
            if (!a.email) {
              hasError = true;
              errors.attendees[i].email = fbt("Please add email");
            } else if (!isEmail(a.email)) {
              hasError = true;
              errors.attendees[i].email = fbt("Please enter valid email");
            }
          } else if (a.email) {
            if (!a.name) {
              hasError = true;
              errors.attendees[i].name = fbt("Please add name");
            }
          }
        });

        if (!hasError) {
          return {};
        }

        return errors;
      },
      onSubmit: async (v) => {
        const hasAttendees = v.attendees.some((a) => a.name && a.email);

        if (!hasAttendees) {
          onComplete(false);
          return;
        }

        await sendOrderInvites(order.id, {
          ...v,
          attendees: v.attendees.filter((a) => a.name),
        });
        onComplete(true);
      },
    });

  return (
    <Container>
      <Text color="muted">{fbt("Message")}</Text>
      <TextArea
        rows={4}
        placeholder={fbt(
          "i.e. Meet me at " +
            fbt.param("offeringName", offeringName) +
            " for our meeting at 0930."
        )}
        value={values.message}
        onChange={(m) => setFieldValue("message", m)}
      />
      <Spacer size={16} />
      {inviteRange.map((i) => (
        <Fragment key={i}>
          <AttendeeInput
            index={i}
            errors={errors?.attendees?.[i] as any}
            attendee={values.attendees[i]}
            onChangeName={(_i, v) => setFieldValue(`attendees[${i}].name`, v)}
            onChangeEmail={(_i, v) => setFieldValue(`attendees[${i}].email`, v)}
            onChangePhoneNumber={(_i, v) =>
              setFieldValue(`attendees[${i}].phone`, v)
            }
          />
          <Spacer size={16} />
          {mq.device !== "desktop" && i !== numberOfInvites - 1 && (
            <Fragment>
              <Divider />
              <Spacer size={16} />
            </Fragment>
          )}
        </Fragment>
      ))}
      <Spacer size={24} />
      {errors.message && <Text color="error">{errors.message}</Text>}
      <Spacer size={24} />
      <Spacer />
      <Row justifyContent="flex-end">
        <Container flex={1} maxWidth={240}>
          <Button
            disabled={isSubmitting}
            onPress={submitForm}
            title={fbt("Send Invitations")}
          />
        </Container>
      </Row>
    </Container>
  );
}

export function getOrderInvitesAttendees(invites: OrderInvites[]) {
  let attendees: InviteAttendeeInput[] = [];

  for (const invite of invites) {
    attendees = attendees.concat(invite.attendees);
  }

  return attendees;
}

export function getInvitationAttendeeListLabel(
  existingInvites: OrderInvites[],
  fbt: FBT
) {
  let str = "";

  const attendees = getOrderInvitesAttendees(existingInvites);

  for (let i = 0; i < attendees.length; i++) {
    const attendee = attendees[i];
    const first = i === 0;
    const last = i === attendees.length - 1;

    if (first) {
      str += `${attendee.name}`;
    } else if (last) {
      str += ` ${fbt("and")} ${attendee.name}`;
    } else {
      str += `, ${attendee.name}`;
    }
  }

  return str;
}
interface AttendeeInputProps {
  index: number;
  errors?: InviteAttendeeInput;
  attendee: InviteAttendeeInput;
  onChangeName: (index: number, name: string) => void;
  onChangeEmail: (index: number, email: string) => void;
  onChangePhoneNumber: (index: number, phone: string) => void;
}

function AttendeeInput(props: AttendeeInputProps) {
  const mq = useMediaQuery();
  const {
    index,
    errors,
    attendee,
    onChangeEmail,
    onChangeName,
    onChangePhoneNumber,
  } = props;
  const { fbt } = useI18n();

  if (mq.device === "desktop") {
    return (
      <Row>
        <Container flex={2}>
          <Text color="muted">{fbt("Name")}</Text>
          <TextInput
            value={attendee.name}
            onChange={(v) => onChangeName(index, v)}
          />
          <Spacer size={4} />
          {errors?.name && <Text color="error">{errors.name}</Text>}
        </Container>
        <Spacer size={8} direction="horizontal" />
        <Container flex={3}>
          <Text color="muted">{fbt("Email")}</Text>
          <TextInput
            direction="ltr"
            value={attendee.email}
            onChange={(v) => onChangeEmail(index, v)}
          />
          <Spacer size={4} />
          {errors?.email && <Text color="error">{errors.email}</Text>}
        </Container>
        <Spacer size={8} direction="horizontal" />
        <Container flex={1} maxWidth={200}>
          <Text color="muted">{fbt("Phone (optional)")}</Text>
          <TextInput
            value={attendee.phone}
            onChange={(v) => onChangePhoneNumber(index, v)}
          />
        </Container>
      </Row>
    );
  }

  return (
    <Container>
      <Text color="muted">{fbt("Name")}</Text>
      <TextInput
        value={attendee.name}
        onChange={(v) => onChangeName(index, v)}
      />
      <Spacer size={4} />
      {errors?.name && <Text color="error">{errors.name}</Text>}
      <Spacer size={8} />
      <Row>
        <Container flex={1} maxWidth="49%">
          <Text color="muted">{fbt("Email")}</Text>
          <TextInput
            direction="ltr"
            value={attendee.email}
            onChange={(v) => onChangeEmail(index, v)}
          />
          <Spacer size={4} />
          {errors?.email && <Text color="error">{errors.email}</Text>}
        </Container>
        <Spacer size={24} direction="horizontal" />
        <Container flex={1} maxWidth="49%">
          <Text color="muted">{fbt("Phone (optional)")}</Text>
          <TextInput
            value={attendee.phone}
            onChange={(v) => onChangePhoneNumber(index, v)}
          />
        </Container>
      </Row>
    </Container>
  );
}

interface PaymentDetailsProps {
  confirmedBookingWoPayment: boolean;
  paymentMethodDetails: PaymentMethod | null;
}

export function PaymentDetails(props: PaymentDetailsProps) {
  const { confirmedBookingWoPayment, paymentMethodDetails } = props;
  const { fbt } = useI18n();
  if (confirmedBookingWoPayment) {
    return <Text size="sm">{fbt("Confirmed without payment")}</Text>;
  }

  if (!paymentMethodDetails || !paymentMethodDetails.card) {
    return <Text size="sm">{fbt("N/A")}</Text>;
  }

  const { last4, network, networkLogoUrl } = paymentMethodDetails.card;

  if (!networkLogoUrl) {
    return (
      <Text size="sm">{fbt("Ending with " + fbt.param("last4", last4))}</Text>
    );
  }

  return (
    <Container>
      <Row alignItems="center">
        <img src={networkLogoUrl} alt={network} style={{ width: 34 }} />
        <Spacer size={10} direction="horizontal" />
        <Text>{fbt("Ending with " + fbt.param("last4", last4))}</Text>
      </Row>
    </Container>
  );
}

export function PaymentDetailsMembership(props: PaymentDetailsProps) {
  const { confirmedBookingWoPayment, paymentMethodDetails } = props;
  const { fbt } = useI18n();
  if (confirmedBookingWoPayment) {
    return <div>{fbt("Confirmed without payment")}</div>;
  }

  if (!paymentMethodDetails || !paymentMethodDetails.card) {
    return <div>{fbt("N/A")}</div>;
  }

  const { last4, network, networkLogoUrl } = paymentMethodDetails.card;

  if (!networkLogoUrl) {
    return <div>{fbt("Ending with " + fbt.param("last4", last4))}</div>;
  }

  return (
    <Container>
      <Row alignItems="center">
        <img src={networkLogoUrl} alt={network} style={{ width: 34 }} />
        <Spacer size={10} direction="horizontal" />
        <div className={styles.customerInfoText}>
          {fbt("Ending with " + fbt.param("last4", last4))}
        </div>
      </Row>
    </Container>
  );
}

interface OrderPaymentMethodProps {
  order: Order;
}

export function OrderPaymentMethod(props: OrderPaymentMethodProps) {
  const { order } = props;
  const { fbt } = useI18n();
  return (
    <Fragment>
      <Text color="muted" size="sm">
        {fbt("Payment method")}
      </Text>
      <Spacer size={8} />
      <PaymentDetails
        confirmedBookingWoPayment={false}
        paymentMethodDetails={order.paymentMethodDetails}
      />
    </Fragment>
  );
}

export function OrderPaymentMethodMembership(props: OrderPaymentMethodProps) {
  const { order } = props;
  const { fbt } = useI18n();
  return (
    <Fragment>
      <div style={{ fontWeight: 600 }}>{fbt("Payment method")}</div>
      <Spacer size={8} />
      <PaymentDetailsMembership
        confirmedBookingWoPayment={false}
        paymentMethodDetails={order.paymentMethodDetails}
      />
    </Fragment>
  );
}

interface OrderGuestDetailsProps {
  order: Order;
}

export function OrderGuestDetails(props: OrderGuestDetailsProps) {
  const { order } = props;
  const { fbt } = useI18n();
  return (
    <div>
      <Text color="muted" size="sm">
        {fbt("Your details")}
      </Text>
      <Spacer size={8} />
      <div>
        <Text>{order.fullName}</Text>
      </div>
      <div>
        <Text>{order.companyName}</Text>
      </div>
      <div>
        <Text>{order.phoneNumber}</Text>
      </div>
      <div>
        <Text>{order.email}</Text>
      </div>
    </div>
  );
}

export function OrderGuestDetailsMembership(props: OrderGuestDetailsProps) {
  const { order } = props;
  const { fbt } = useI18n();
  return (
    <Fragment>
      <div style={{ fontWeight: 600 }}>{fbt("Your details")}</div>

      <Spacer size={8} />
      <div className={styles.customerInfoText}>{order.fullName}</div>
      <div className={styles.customerInfoText}>{order.companyName}</div>
      <div className={styles.customerInfoText}>{order.phoneNumber}</div>
      <div className={styles.customerInfoText}>{order.email}</div>
    </Fragment>
  );
}

interface OrderTaxProps {
  order: Order;
}

export function OrderTax(props: OrderTaxProps) {
  const { order } = props;
  const { fbt } = useI18n();
  if (!order.billingDetails?.tax) {
    return null;
  }

  return (
    <Fragment>
      <Text color="muted" size="sm">
        {fbt("Tax / VAT / ID number")}
      </Text>
      <Spacer size={8} />
      <Text>{order.billingDetails.tax}</Text>
    </Fragment>
  );
}

interface OrderBillingDetailsProps {
  order: Order;
}

export function OrderBillingDetails(props: OrderBillingDetailsProps) {
  const { order } = props;
  const { fbt } = useI18n();
  const { size } = useMediaQuery();
  if (!order.billingDetails) {
    return null;
  }

  const hasBillingContactDetails = !!(
    order.billingDetails.fullName &&
    order.billingDetails.phone &&
    order.billingDetails.email
  );

  return (
    <Fragment>
      <Text color="muted" size="sm">
        {fbt("Billing details")}
      </Text>
      <Spacer size={8} />
      <div className={size === "xl" ? "d-flex gap-5" : undefined}>
        {hasBillingContactDetails && (
          <>
            <div>
              <div>
                <Text>{order.billingDetails.fullName}</Text>
              </div>
              <div>
                <Text>{order.billingDetails.phone}</Text>
              </div>
              <div>
                <Text>{order.billingDetails.email}</Text>
              </div>
            </div>
            <br />
          </>
        )}

        <div>
          <Text>
            {order.billingDetails.streetAddress}, {order.billingDetails.city},{" "}
            {order.billingDetails.state
              ? `${order.billingDetails.state}, `
              : ""}
            {order.billingDetails.country}
          </Text>
        </div>
      </div>
    </Fragment>
  );
}

export function OrderBillingDetailsMembership(props: OrderBillingDetailsProps) {
  const { order } = props;
  const { fbt } = useI18n();
  const { size } = useMediaQuery();
  if (!order.billingDetails) {
    return null;
  }

  const hasBillingContactDetails = !!(
    order.billingDetails.fullName &&
    order.billingDetails.phone &&
    order.billingDetails.email
  );

  return (
    <Fragment>
      <div style={{ fontWeight: 600 }}>{fbt("Billing details")}</div>
      <Spacer size={8} />
      <div className={size === "xl" ? "d-flex gap-5" : undefined}>
        {hasBillingContactDetails && (
          <>
            <div>
              <div className={styles.customerInfoText}>
                {order.billingDetails.fullName}
              </div>
              <div className={styles.customerInfoText}>
                {order.billingDetails.phone}
              </div>
              <div className={styles.customerInfoText}>
                {order.billingDetails.email}
              </div>
            </div>
            <br />
          </>
        )}

        <div>
          <div className={styles.customerInfoText}>
            {order.billingDetails.streetAddress}
          </div>
          <div className={styles.customerInfoText}>
            {order.billingDetails.city},{" "}
            {order.billingDetails.state
              ? `${order.billingDetails.state}, `
              : ""}{" "}
            {order.billingDetails.postalCode
              ? `${order.billingDetails.postalCode}`
              : ""}
            <div className={styles.customerInfoText}>
              {order.billingDetails.country}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
}
